import { useState, useRef, useEffect } from "react"
import { Check, ChevronsUpDown, Plus, Loader2, ChevronDown, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import hive, { type TickerLookupResult, type Company, depositCash, withdrawCash } from "@/lib/api/hive"
import { type Stock } from "@/types/stock"
import { ExternalLookupDialog } from "./external-lookup-dialog"
import { BulkTransactionDialog } from "./bulk-transaction-dialog"
import { useCurrency } from "@/context/currency/currency-context"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { Analytics } from "@/lib/analytics"

type TransactionMode = "BUY" | "SELL" | "DEPOSIT" | "WITHDRAW"

interface TransactionFormProps {
	portfolioId: string
	onTransactionAdded: () => void
	token: string
	cashBalance?: number
	cashCurrency?: string
	cashEnabled?: boolean
	onCashChange?: () => void
}

export function TransactionForm({
	portfolioId,
	onTransactionAdded,
	token,
	cashBalance,
	cashCurrency,
	cashEnabled,
	onCashChange,
}: TransactionFormProps) {
	const { format, convert, localFormat } = useCurrency()
	const [open, setOpen] = useState(false)
	const [selectedStock, setSelectedStock] = useState<Stock | null>(null)
	const [shares, setShares] = useState("")
	const [transactionType, setTransactionType] = useState<TransactionMode>("BUY")
	const [showAdvanced, setShowAdvanced] = useState(false)
	const [price, setPrice] = useState("")
	const [date, setDate] = useState("")
	const [searchQuery, setSearchQuery] = useState("")
	const [searchResults, setSearchResults] = useState<Stock[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [externalLookupOpen, setExternalLookupOpen] = useState(false)
	// Cash-specific state
	const [cashAmount, setCashAmount] = useState("")
	const [cashInputCurrency, setCashInputCurrency] = useState(cashCurrency ?? "USD")
	const [cashDescription, setCashDescription] = useState("")

	const sharesInputRef = useRef<HTMLInputElement>(null)

	const isCashMode = transactionType === "DEPOSIT" || transactionType === "WITHDRAW"

	// Sync cashInputCurrency when cashCurrency prop updates
	useEffect(() => {
		if (cashCurrency) setCashInputCurrency(cashCurrency)
	}, [cashCurrency])

	const handleTickerAdded = (ticker: TickerLookupResult, company?: Company) => {
		const stock: Stock = company
			? {
					ticker: company.symbol,
					name: company.company_name,
					exchange: company.exchange,
					price: company.stock.price || 0,
					marketCap: (company.stock.market_cap || 0).toString(),
					sector: company.sector || "Unknown",
					dividendPerShare: company.stock.last_div || 0,
					dividendYield:
						(company.stock.price || 0) > 0
							? ((company.stock.last_div || 0) / (company.stock.price || 1)) * 100
							: 0,
					dividendGrowth5y: 0,
					frequency: "Quarterly" as const,
					currency: company.currency,
				}
			: {
					ticker: ticker.symbol,
					name: ticker.name,
					exchange: ticker.exchange,
					price: 0,
					marketCap: "0",
					sector: "Unknown",
					dividendPerShare: 0,
					dividendYield: 0,
					dividendGrowth5y: 0,
					frequency: "Quarterly" as const,
				}
		setSelectedStock(stock)
		setOpen(false)
		setTimeout(() => sharesInputRef.current?.focus(), 0)
	}

	useEffect(() => {
		const search = async () => {
			if (searchQuery.length < 2) {
				setSearchResults([])
				return
			}
			setIsLoading(true)
			try {
				const results = await hive.searchCompanies(searchQuery)
				const detailedResults = await Promise.all(
					results.slice(0, 10).map(async (r) => {
						try {
							const overview = await hive.getDividendOverview(r.symbol, r.exchange)
							return {
								ticker: r.symbol,
								name: r.company_name,
								exchange: r.exchange,
								price: r.stock.price || 0,
								marketCap: (r.stock.market_cap || 0).toString(),
								sector: "Unknown",
								dividendPerShare: overview.last_12m_dividend || 0,
								dividendYield:
									(r.stock.price || 0) > 0
										? ((overview.last_12m_dividend || 0) / (r.stock.price || 1)) * 100
										: 0,
								dividendGrowth5y: (overview.last_12m_dividend_growth || 0) * 100,
								frequency: "Quarterly" as const,
								currency: r.currency,
							}
						} catch {
							return {
								ticker: r.symbol,
								name: r.company_name,
								exchange: r.exchange,
								price: r.stock.price || 0,
								marketCap: (r.stock.market_cap || 0).toString(),
								sector: "Unknown",
								dividendPerShare: r.stock.last_div || 0,
								dividendYield:
									(r.stock.price || 0) > 0
										? ((r.stock.last_div || 0) / (r.stock.price || 1)) * 100
										: 0,
								dividendGrowth5y: 0,
								frequency: "Quarterly" as const,
								currency: r.currency,
							}
						}
					}),
				)
				setSearchResults(detailedResults)
			} catch (error) {
				console.error("Search failed", error)
			} finally {
				setIsLoading(false)
			}
		}

		const debounceTimer = setTimeout(search, 300)
		return () => clearTimeout(debounceTimer)
	}, [searchQuery])

	const handleAdd = async () => {
		if (isCashMode) {
			const amount = parseFloat(cashAmount)
			if (!cashAmount || isNaN(amount) || amount <= 0) return false
			setIsSubmitting(true)
			try {
				const dateParam = date ? date : new Date().toISOString().split("T")[0]
				if (transactionType === "DEPOSIT") {
					await depositCash(portfolioId, amount, cashInputCurrency, dateParam, cashDescription || undefined)
					Analytics.capture("Cash Deposited", { amount, currency: cashInputCurrency })
				} else {
					await withdrawCash(portfolioId, amount, cashInputCurrency, dateParam, cashDescription || undefined)
					Analytics.capture("Cash Withdrawn", { amount, currency: cashInputCurrency })
				}
				setCashAmount("")
				setCashDescription("")
				setDate("")
				onCashChange?.()
				return true
			} catch (error) {
				console.error("Failed to record cash transaction:", error)
				return false
			} finally {
				setIsSubmitting(false)
			}
		}

		if (!selectedStock || !shares || parseFloat(shares) <= 0) {
			return false
		}
		if (price && parseFloat(price) <= 0) {
			return false
		}

		setIsSubmitting(true)

		try {
			const fallbackPrice = selectedStock.price > 0 ? selectedStock.price : undefined
			await hive.createTransaction(
				portfolioId,
				{
					ticker: selectedStock.ticker,
					exchange: selectedStock.exchange,
					type: transactionType as "BUY" | "SELL",
					shares: parseFloat(shares),
					price: price ? parseFloat(price) : fallbackPrice,
					date: date ? new Date(date).toISOString() : undefined,
				},
				token,
			)

			// Reset form
			const ticker = selectedStock.ticker
			const type = transactionType
			const sharesVal = parseFloat(shares)
			setSelectedStock(null)
			setShares("")
			setPrice("")
			setDate("")
			setShowAdvanced(false)

			Analytics.capture("Transaction Added", { type, ticker, shares: sharesVal })
			onTransactionAdded()
			return true
		} catch (error) {
			console.error("Failed to add transaction:", error)
			return false
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			handleAdd().then((success) => {
				if (success && !isCashMode) {
					setTimeout(() => setOpen(true), 0)
				}
			})
		}
	}

	// Determine if the buy amount would exceed available cash
	const buyTotal =
		selectedStock && shares && parseFloat(shares) > 0
			? parseFloat(shares) * (price ? parseFloat(price) : selectedStock.price)
			: 0
	const availableCashConverted =
		cashEnabled && cashBalance != null && cashCurrency ? convert(cashBalance, cashCurrency) : null
	const wouldExceedCash = availableCashConverted != null && buyTotal > 0 && buyTotal > availableCashConverted

	return (
		<Card className="h-fit bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardHeader className="flex flex-row items-center justify-between space-y-0">
				<div>
					<CardTitle className="text-sky-950 dark:text-sky-50">Add Transaction</CardTitle>
					<CardDescription>Record a buy, sell, or cash movement for your portfolio.</CardDescription>
				</div>
				<BulkTransactionDialog
					portfolioId={portfolioId}
					token={token}
					cashCurrency={cashCurrency}
					onSuccess={onTransactionAdded}
				/>
			</CardHeader>

			<CardContent className="space-y-4 pt-4">
				{/* Transaction Type Toggle */}
				<div className="grid grid-cols-2 sm:flex gap-1 sm:gap-2">
					<Button
						type="button"
						variant={transactionType === "BUY" ? "default" : "outline"}
						className={cn(
							"flex-1",
							transactionType === "BUY" && "bg-green-600 hover:bg-green-700 text-white",
						)}
						onClick={() => setTransactionType("BUY")}
					>
						Buy
					</Button>
					<Button
						type="button"
						variant={transactionType === "SELL" ? "default" : "outline"}
						className={cn("flex-1", transactionType === "SELL" && "bg-red-600 hover:bg-red-700 text-white")}
						onClick={() => setTransactionType("SELL")}
					>
						Sell
					</Button>
					<Button
						type="button"
						variant={transactionType === "DEPOSIT" ? "default" : "outline"}
						className={cn(
							"flex-1",
							transactionType === "DEPOSIT" && "bg-sky-600 hover:bg-sky-700 text-white",
						)}
						onClick={() => setTransactionType("DEPOSIT")}
					>
						Deposit
					</Button>
					<Button
						type="button"
						variant={transactionType === "WITHDRAW" ? "default" : "outline"}
						className={cn(
							"flex-1",
							transactionType === "WITHDRAW" && "bg-orange-500 hover:bg-orange-600 text-white",
						)}
						onClick={() => setTransactionType("WITHDRAW")}
					>
						Withdraw
					</Button>
				</div>

				{isCashMode ? (
					/* Cash Mode Fields */
					<div className="space-y-4">
						<div className="flex flex-col md:flex-row gap-4">
							<div className="flex-1 space-y-1.5">
								<Label htmlFor="cash-amount">Amount</Label>
								<Input
									id="cash-amount"
									type="number"
									placeholder="0.00"
									value={cashAmount}
									onChange={(e) => setCashAmount(e.target.value)}
									onKeyDown={handleKeyDown}
									min="0.01"
									step="0.01"
								/>
							</div>
							<div className="w-full md:w-28 space-y-1.5">
								<Label htmlFor="cash-currency">Currency</Label>
								<Input
									id="cash-currency"
									type="text"
									placeholder="USD"
									value={cashInputCurrency}
									onChange={(e) => setCashInputCurrency(e.target.value.toUpperCase())}
									maxLength={3}
								/>
							</div>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="cash-description">Description (optional)</Label>
							<Input
								id="cash-description"
								type="text"
								placeholder="e.g. Monthly contribution"
								value={cashDescription}
								onChange={(e) => setCashDescription(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="cash-date">Date</Label>
							<Input id="cash-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
							<p className="text-xs text-muted-foreground">Leave empty for today</p>
						</div>
					</div>
				) : (
					/* Stock Mode Fields */
					<>
						<div className="flex flex-col md:flex-row gap-4 items-end">
							{/* STOCK SEARCH */}
							<div className="flex-1 space-y-1.5 w-full">
								<Label>Company</Label>
								<Popover open={open} onOpenChange={setOpen}>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											role="combobox"
											aria-expanded={open}
											className="justify-between w-full font-normal"
										>
											{selectedStock ? selectedStock.name : "Select stock..."}
											<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-[300px] p-0" align="start">
										<Command shouldFilter={false}>
											<CommandInput
												placeholder="Search company..."
												value={searchQuery}
												onValueChange={setSearchQuery}
											/>
											<CommandList>
												{isLoading && (
													<div className="flex items-center justify-center p-4">
														<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
													</div>
												)}
												<CommandEmpty>
													{searchQuery.length < 2 ? "Type to search..." : "No stock found."}
												</CommandEmpty>
												<CommandGroup>
													{searchResults.map((stock) => (
														<CommandItem
															key={`${stock.ticker}-${stock.exchange}`}
															value={`${stock.ticker}-${stock.exchange}`}
															onSelect={() => {
																setSelectedStock(stock)
																setOpen(false)
																setTimeout(() => sharesInputRef.current?.focus(), 0)
															}}
														>
															<Check
																className={cn(
																	"mr-2 h-4 w-4",
																	selectedStock?.ticker === stock.ticker
																		? "opacity-100"
																		: "opacity-0",
																)}
															/>
															<div className="flex flex-col">
																<span>{stock.name}</span>
																<span className="text-xs text-muted-foreground">
																	{stock.ticker} • {stock.exchange} •{" "}
																	{localFormat(stock.price, stock.currency)}
																</span>
															</div>
														</CommandItem>
													))}
												</CommandGroup>

												{searchQuery.length >= 2 && (
													<CommandGroup>
														<CommandItem
															onSelect={() => {
																setExternalLookupOpen(true)
																setOpen(false)
															}}
															className="text-sky-600 font-medium cursor-pointer"
														>
															<Search className="mr-2 h-4 w-4" />
															Can't find my company
														</CommandItem>
													</CommandGroup>
												)}
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</div>

							{/* SHARES INPUT */}
							<div className="w-full md:w-32 space-y-1.5">
								<Label htmlFor="shares">Shares</Label>
								<Input
									ref={sharesInputRef}
									id="shares"
									type="number"
									placeholder="0"
									value={shares}
									onChange={(e) => setShares(e.target.value)}
									onKeyDown={handleKeyDown}
									min="0"
									step="0.01"
								/>
							</div>
						</div>

						{/* Available cash indicator for BUY */}
						{transactionType === "BUY" && cashEnabled && availableCashConverted != null && (
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground">
									Available cash: {format(availableCashConverted, { skipConversion: true })}
								</p>
								{wouldExceedCash &&
									(!selectedStock?.currency || selectedStock.currency === cashCurrency) && (
										<p className="text-xs text-amber-600 dark:text-amber-400">
											This purchase exceeds your available cash balance.
										</p>
									)}
							</div>
						)}

						{/* Advanced Toggle */}
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => setShowAdvanced(!showAdvanced)}
							className="w-full text-xs text-muted-foreground hover:text-sky-600"
						>
							<ChevronDown
								className={cn("h-4 w-4 mr-1 transition-transform", showAdvanced && "rotate-180")}
							/>
							{showAdvanced ? "Hide" : "Show"} Advanced Options
						</Button>

						{/* Advanced Fields */}
						{showAdvanced && (
							<div className="flex flex-col md:flex-row gap-4 pt-2 border-t">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="price">Price per Share</Label>
									<Input
										id="price"
										type="number"
										placeholder="Current market price"
										value={price}
										onChange={(e) => setPrice(e.target.value)}
										min="0.01"
										step="0.01"
									/>
									<p className="text-xs text-muted-foreground">Leave empty to use current price</p>
								</div>

								<div className="flex-1 space-y-1.5">
									<Label htmlFor="date">Date</Label>
									<Input
										id="date"
										type="date"
										value={date}
										onChange={(e) => setDate(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground">Leave empty for today</p>
								</div>
							</div>
						)}
					</>
				)}

				{/* Submit Button */}
				{isCashMode ? (
					<Button
						className={cn(
							"w-full text-white",
							transactionType === "DEPOSIT"
								? "bg-sky-600 hover:bg-sky-700"
								: "bg-orange-500 hover:bg-orange-600",
						)}
						onClick={handleAdd}
						disabled={!cashAmount || parseFloat(cashAmount) <= 0 || isSubmitting}
					>
						{isSubmitting ? (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						) : (
							<Plus className="h-4 w-4 mr-2" />
						)}
						{transactionType === "DEPOSIT" ? "Deposit" : "Withdraw"}
						{cashAmount && parseFloat(cashAmount) > 0
							? ` ${cashInputCurrency} ${parseFloat(cashAmount).toLocaleString()}`
							: ""}
					</Button>
				) : (
					<Button
						className={cn(
							"w-full text-white",
							transactionType === "BUY"
								? "bg-green-600 hover:bg-green-700"
								: "bg-red-600 hover:bg-red-700",
						)}
						onClick={handleAdd}
						disabled={!selectedStock || !shares || parseFloat(shares) <= 0 || isSubmitting}
					>
						{isSubmitting ? (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						) : (
							<Plus className="h-4 w-4 mr-2" />
						)}
						{transactionType === "BUY" ? "Buy" : "Sell"}{" "}
						{shares && parseFloat(shares) > 0 ? `${shares} shares` : ""}
					</Button>
				)}

				<ExternalLookupDialog
					open={externalLookupOpen}
					onOpenChange={setExternalLookupOpen}
					onTickerAdded={handleTickerAdded}
					initialQuery={searchQuery}
				/>
			</CardContent>
		</Card>
	)
}
